import React, { Dispatch, SetStateAction } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { LooksOne } from '@mui/icons-material';
import { SelectOption, TabsOption } from 'global';
import { aftermarketProductsApi } from 'modules/aftermarket/products/api';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { AftermarketLicenceType, AftermarketProductType } from '@trnsact/trnsact-shared-types';
import {
  AddonsOptionsResponse,
  AddonsOptionsVariables,
  AftermarketProductCatalogDetails,
  AftermarketProductCatalogForm,
  AftermarketProductConfigTabs,
  AftermarketProductConfigurator,
  ChangelogListing,
  Configuration,
  mapListingToConfiguration,
  ProductConfig,
} from 'modules/aftermarket';

interface Props {
  product: any;
  canWrite: boolean;
  accountId: string;
  selectedOptions: SelectOption[];
  baseOrOption: AftermarketProductType;
  existingUniqueCriteriaFacts: string[];
  aftermarketProductId: string | undefined;
  mappedProductConfigForListing: ProductConfig[];
  productCatalog: AftermarketProductCatalogDetails;
  setSelectedOptions: Dispatch<SetStateAction<SelectOption[]>>;
  setConfigurations: Dispatch<SetStateAction<Configuration[]>>;
  setProductCatalog: Dispatch<SetStateAction<AftermarketProductCatalogDetails>>;
}

export const ConfigurationSection = ({
  product,
  canWrite,
  accountId,
  baseOrOption,
  productCatalog,
  selectedOptions,
  setProductCatalog,
  setConfigurations,
  setSelectedOptions,
  aftermarketProductId,
  existingUniqueCriteriaFacts,
  mappedProductConfigForListing,
}: Props) => {
  const aftermarketLicenceType = useSelector(portalConfigurationsSelectors.aftermarketLicenceType);

  const { data: addonsOptions, loading: isAddonsOptionsLoading } = useQuery<
    AddonsOptionsResponse,
    AddonsOptionsVariables
  >(aftermarketProductsApi.getAddonsOptionsByAccountId, {
    variables: {
      accountId,
    },
  });

  const tabs: TabsOption<AftermarketProductConfigTabs>[] = [
    {
      value: AftermarketProductConfigTabs.Base,
      label: `Base ${baseOrOption === AftermarketProductType.Product ? 'Product' : 'Option'}`,
      component: (
        <RenderWithCondition
          fallBack={<Typography>Managed by API</Typography>}
          condition={!product?.aftermarketVendorApiChannel && !!mappedProductConfigForListing.length}
        >
          <Box sx={{ marginTop: '0.5rem' }}>
            <AftermarketProductConfigurator
              isReadOnly={!canWrite}
              aftermarketLicenceType={aftermarketLicenceType}
              initialConfigKeys={existingUniqueCriteriaFacts}
              initialConfigurationList={mappedProductConfigForListing}
              onSubscribe={listOfConfiguration => {
                setConfigurations(
                  listOfConfiguration.map((listItem, index) => mapListingToConfiguration(listItem, index))
                );
              }}
            />
          </Box>
        </RenderWithCondition>
      ),
    },
    {
      label: 'Addons',
      value: AftermarketProductConfigTabs.Addons,
      disabled: baseOrOption === AftermarketProductType.Addon,
      isVisible: aftermarketLicenceType === AftermarketLicenceType.Enterprise,
      component: (
        <RenderWithCondition condition={!product?.aftermarketVendorApiChannel} fallBack={<div>Managed by API</div>}>
          <Stack gap="1rem" marginTop="0.5rem">
            <Typography variant="subtitle1" color="primary">
              <LooksOne /> Select Options on this Product
            </Typography>

            <Autocomplete
              multiple
              size="small"
              value={selectedOptions}
              loading={isAddonsOptionsLoading}
              options={addonsOptions?.options ?? []}
              disabled={!canWrite}
              onChange={(_, newValues) => {
                setSelectedOptions(newValues);
              }}
              renderInput={params => (
                <TextField {...params} label="Select Options on this Product" variant="outlined" />
              )}
            />
          </Stack>
        </RenderWithCondition>
      ),
    },
    {
      value: AftermarketProductConfigTabs.ProductCatalog,
      label: 'Product Catalog',
      component: (
        <Box marginTop="0.5rem">
          <AftermarketProductCatalogForm
            canWrite={canWrite}
            value={productCatalog}
            onChange={(key, value) => {
              setProductCatalog(prev => ({ ...prev, [key]: value }));
            }}
          />
        </Box>
      ),
    },
    {
      label: 'Change history',
      isVisible: !!aftermarketProductId,
      value: AftermarketProductConfigTabs.Changelog,
      component: (
        <Box marginTop="0.5rem">
          <ChangelogListing productId={aftermarketProductId!} />
        </Box>
      ),
    },
  ];

  return (
    <>
      <Typography variant="subtitle2" component="span">
        Configuration
      </Typography>

      <TabsContainer<AftermarketProductConfigTabs>
        tabs={tabs}
        forceTabValue={
          product?.aftermarketVendorApiChannel
            ? AftermarketProductConfigTabs.ProductCatalog
            : AftermarketProductConfigTabs.Base
        }
      />
    </>
  );
};
